// KYT v2 · SCREENING ASSESSMENT (Astra's `risk-detail`) — one incoming payment, opened over the list (UI rebuild batch 4;
// the KYT lane's since 27 Oct 2026; ?payment=<id>, so Back closes it and a link can land on it).
//
// Astra: "lead with verdict and missing checks; order evidence by the decision it supports: sanctions, source, identity,
// destination". Under the operator's ruling ("I just need proper attribution, decision will be manual — NEVER add
// verdicts") it leads with WHAT IS UNRESOLVED — why this payment is here, what its check did not cover, and the lane's own
// action words — then the facts in Astra's order. The whole picture (the source split, every source by name, the deep
// check, what was read) is the lane's own trace, one tap away: it is reused, never redrawn.
import type { KytPayment } from '../../../lib/api';
import { fmtAmount, fmtStamp } from '../../../lib/format';
import { Pill, shortAddress } from '../../kit';
import { Address, DetailSheet, Fact, Label } from '../kit';
import { Exposed, Finding, LevelTag, Sender, Share, Who, useSheetFocus } from './parts';
import {
  ALSO_WORDS, STANDING_WORDS, actionWords, alsoAdverse, capital, explorer, mainRisk, mask, notFullWords, reasonLine, sanctionsWords, share,
  type SenderName,
} from '../../../screens/Kyt';

/** What is still open on this payment, in the lane's words — each a line, the hold in red. Empty for a clean payment
 *  whose check read everything. */
function unresolved(p: KytPayment): Array<{ text: string; tone?: 'bad' | 'warn' | 'hint' }> {
  const out: Array<{ text: string; tone?: 'bad' | 'warn' | 'hint' }> = [];
  const why = reasonLine(p);
  if (why && !(p.level === 'clean' && p.caveats.length === 0)) out.push({ text: capital(why) });
  const notFull = notFullWords(p);
  if (notFull) out.push({ text: notFull, tone: 'warn' });
  const act = actionWords(p);
  const hold = p.action === 'do_not_move' || p.action === 'keep_apart';
  if (act) out.push({ text: act, tone: hold ? 'bad' : undefined });
  return out;
}

/** The funds by kind of source when there is no trace to split: what the check could say. */
function sourcesWords(p: KytPayment): string {
  if (p.level === 'pending') return 'Not known yet — its check has not run';
  if (p.small) return 'Not known — GL was not asked';
  if (!p.sourcesRead) return "Not known — GL's sources were not read";
  return p.funds ?? 'Older check — summary only';
}
const SOURCE_WORD: Record<string, string> = { sanctions: 'Sanctions', criminal: 'Criminal', risky: 'Risky', unknown: 'Unknown', known: 'Known' };
/** The funds by kind of source, ONE LINE A KIND (1 Nov 2026: five shares in one wrapping value were five facts in a cell), and
 *  what GL did not read as a line of its own, in amber — it is not a sixth kind of source. */
function SourceLines({ p }: { p: KytPayment }) {
  const t = p.trace;
  if (!t) return <span className="block">{sourcesWords(p)}</span>;
  return (
    <span className="block">
      {t.groups.map((g) => (
        <span key={g.group} className="block"><span className="text-tg-hint">{SOURCE_WORD[g.group] ?? g.group}</span> <span className="font-mono tabular">{share(g.pct)}</span></span>
      ))}
      {t.unread > 0 && <span className="block text-meta text-warn">{share(t.unread)} not read by GL</span>}
    </span>
  );
}

export function Assessment({ p, gl, onClose, onTrace }: { p: KytPayment; gl: SenderName | null; onClose: () => void; onTrace: () => void }) {
  useSheetFocus(true);
  const open = unresolved(p);
  const s = sanctionsWords(p);
  const risk = mainRisk(p);
  const ex = explorer(p.chain);
  const hold = p.action === 'do_not_move' || p.action === 'keep_apart';
  return (
    <DetailSheet title="Screening assessment" onClose={onClose}
      footer={
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" className="btn-secondary px-3 text-body max-lg:min-h-11" onClick={onTrace}>Full trace</button>
          {ex && p.txid && (
            <a href={ex.tx(p.txid)} target="_blank" rel="noreferrer" className="btn-link inline-flex items-center px-2 text-body max-lg:min-h-11">Transaction ↗</a>
          )}
        </div>
      }>
      {/* The payment: how much, from whom, into which wallet — then the lane's level word as a tag. */}
      <div className="mb-4 space-y-1">
        <Label>Incoming payment</Label>
        <div className="font-mono tabular text-figure font-semibold">
          {fmtAmount(p.amount, p.asset)} <span className="text-meta font-normal text-tg-hint">{p.asset}</span>
        </div>
        {p.party && <div className="text-body"><Who p={p} /></div>}
        <div className="flex flex-wrap items-center gap-2 text-meta text-tg-hint">
          <span>{p.at ? fmtStamp(p.at) : '—'} · into {p.walletLabel ?? `the ${p.chain} desk wallet`}</span>
          <LevelTag p={p} />
        </div>
      </div>

      {/* WHAT IS UNRESOLVED leads, before any source detail. */}
      <section className={`mb-4 space-y-1 rounded-lg px-4 py-3 text-body ${hold ? 'bg-neg-bg' : 'bg-panel-2'}`} aria-label="Unresolved">
        <Label>Unresolved</Label>
        {open.length > 0
          ? open.map((l) => (
            <p key={l.text} className={l.tone === 'bad' ? 'font-medium text-neg' : l.tone === 'warn' ? 'text-warn' : ''}>{l.text}</p>
          ))
          : <p className="text-tg-hint">Nothing found; every check read.</p>}
      </section>

      {/* The evidence, in the order of the decision it supports: sanctions, source, identity, destination. */}
      <div>
        <Fact label="Sanctions">
          <span className="inline-flex flex-wrap items-center justify-end gap-x-2 gap-y-1">
            <Pill tone={s.tone}>{s.head}</Pill>
            {s.rest && <span className="text-meta text-tg-hint">{s.rest}</span>}
          </span>
        </Fact>
        {/* The risk the payment was rated on, and who; its share and that share in dollars are facts of their own (1 Nov 2026). */}
        <Fact label="Main risk">
          <Finding p={p} table />
          {p.deep && <span className="block text-meta text-tg-hint">deep checked</span>}
        </Fact>
        {risk.pct != null && <Fact label="Share"><Share p={p} /></Fact>}
        {risk.usd != null && <Fact label="Exposure"><Exposed p={p} /></Fact>}
        <Fact label="Source of funds">
          <SourceLines p={p} />
          {p.trace && <span className="block text-meta text-tg-hint">{p.basis === 'tx' ? "this payment's own funds" : "the sender's funds, as rated"}</span>}
        </Fact>
        <Fact label="Booked to">
          {p.party
            ? <><span className="block">{p.party.name}</span><span className="block text-meta text-tg-hint">{p.party.via === 'deal' ? "the wallet's deal" : 'a Books booking'}</span></>
            : <span className="text-tg-hint">not booked yet</span>}
        </Fact>
        <Fact label="Who paid">
          <Sender p={p} gl={gl} />
          {p.customer && p.standing && (
            <span className={`block text-meta ${p.standing === 'blocked' || p.standing === 'declined' ? 'text-warn' : 'text-tg-hint'}`}>{STANDING_WORDS[p.standing]}</span>
          )}
          {p.customer && p.standing === 'verified' && p.own === false && (
            <span className="block text-meta text-tg-hint">named in their chat only</span>
          )}
          {p.customer && p.also && (
            <span className={`block text-meta ${alsoAdverse(p.also) ? 'text-warn' : 'text-tg-hint'}`}>also given by {ALSO_WORDS[p.also.standing]}</span>
          )}
          {!p.customer && p.level !== 'pending' && <span className="block text-meta text-tg-hint">not an onboarded customer</span>}
          {/* A look-alike (address poisoning, 3 Nov 2026): the warning once, the address red and without Copy — a copy button is what the attack relies on. */}
          {p.poisoning && <span className="block text-meta text-neg">Address poisoning — look-alike of {p.poisoning.of}. Never a payee; never copy.</span>}
          {p.poisoning
            ? <span className="mt-1 inline-flex justify-end font-mono text-meta text-neg" data-address="lookalike" title={`Address poisoning — look-alike of ${p.poisoning.of}. Never a payee; never copy.`}>{shortAddress(p.from)}</span>
            : <span className="mt-1 inline-flex justify-end"><Address value={p.from} /></span>}
        </Fact>
        {/* A score is the score of what GL read: this transaction, or the sender's wallet — said, never assumed (Astra). */}
        <Fact label="GL score">
          {p.score != null
            ? <><span className="font-mono tabular">{Math.round(p.score)}</span> of 100{p.band ? ` · ${p.band}` : ''}<span className="block text-meta text-tg-hint">{p.basis === 'tx' ? 'of this transaction' : "of the sender's wallet"}</span></>
            : <span className="text-tg-hint">No GL score</span>}
          {p.thin && <span className="block text-meta text-warn">GL holds little data</span>}
        </Fact>
        <Fact label="Paid into">{p.walletLabel ?? <>Desk wallet <span className="font-mono text-meta text-tg-hint">{p.chain.toUpperCase()}</span></>}</Fact>
        <Fact label="Received">{p.at ? fmtStamp(p.at) : '—'}</Fact>
        <Fact label="Transaction"><span className="font-mono text-meta" title={p.txid}>{mask(p.txid)}</span></Fact>
        <Fact label="KYT group">{p.told ? 'Report delivered' : <span className="text-tg-hint">Not delivered yet</span>}</Fact>
      </div>
    </DetailSheet>
  );
}
